import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import { useWarehouseLocal } from '@/hooks/useWarehouseLocal';
import { apiService } from '@/services/api';
import { ProductOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import StockIndicator from '@/components/atoms/StockIndicator/StockIndicator';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import { Pagination } from '@/components/molecules/Pagination';
import './WarehouseDashboard.scss';

const PAGE_SIZE = 12;

type KindFilter = 'all' | 'drinks' | 'foods';

// Case- and accent-insensitive, so "anejo" finds "Añejo".
const normalize = (text: string) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const WarehouseDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { lastStockUpdate } = useNotifications();

  const { localId, setLocalId, availableLocals, loading: localLoading } = useWarehouseLocal();

  const [products, setProducts] = useState<ProductOut[]>([]);
  const [searchParams] = useSearchParams();
  // ?product=<id> (from the carta's "view stock" links) scrolls to and highlights that product.
  const highlightedProductId = parseInt(searchParams.get('product') || '') || null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [page, setPage] = useState(1);
  // Per-product quantity and note typed in the stock row (e.g. a supplier delivery of 48).
  const [adjustInputs, setAdjustInputs] = useState<Record<number, { quantity: string; note: string }>>({});
  const [showExplainer, setShowExplainer] = useState(() => {
    try {
      return localStorage.getItem('warehouse-explainer-dismissed') !== '1';
    } catch {
      return true;
    }
  });

  const dismissExplainer = () => {
    setShowExplainer(false);
    try {
      localStorage.setItem('warehouse-explainer-dismissed', '1');
    } catch {
      // ignore - per-viewer convenience only
    }
  };

  const loadProducts = useCallback(async () => {
    if (!localId) {
      setLoading(false);
      return;
    }
    try {
      const data = await apiService.getProducts(localId);
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [localId, t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Real-time refresh when another warehouse/organizer/barman user changes stock
  useEffect(() => {
    if (lastStockUpdate && lastStockUpdate.localId === localId) {
      loadProducts();
    }
  }, [lastStockUpdate, localId, loadProducts]);

  const getAdjustInput = (productId: number) => adjustInputs[productId] ?? { quantity: '1', note: '' };

  const setAdjustInput = (productId: number, changes: Partial<{ quantity: string; note: string }>) => {
    setAdjustInputs((prev) => ({ ...prev, [productId]: { ...getAdjustInput(productId), ...changes } }));
  };

  // sign = +1 for an entry (Entrada), -1 for a removal (Salida); the amount is the typed quantity.
  const handleAdjust = async (productId: number, sign: 1 | -1) => {
    const { quantity, note } = getAdjustInput(productId);
    const amount = parseInt(quantity, 10);
    if (!amount || amount <= 0) {
      setError(t('warehouse.dashboard.invalidQuantity'));
      return;
    }
    setBusyId(productId);
    setError(null);
    try {
      const updated = await apiService.adjustProductStock(productId, {
        delta: sign * amount,
        note: note.trim() || undefined,
      });
      setProducts((prev) => prev.map((p) => (p.id === productId ? updated : p)));
      setAdjustInputs((prev) => ({ ...prev, [productId]: { quantity: '1', note: '' } }));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setBusyId(null);
    }
  };

  const filteredProducts = useMemo(() => {
    const term = normalize(search.trim());
    return products.filter((product) => {
      if (kindFilter === 'drinks' && !product.is_drink) return false;
      if (kindFilter === 'foods' && product.is_drink) return false;
      return !term || normalize(product.name).includes(term);
    });
  }, [products, search, kindFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageProducts = filteredProducts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Back to page 1 when the search, filter or Local changes.
  useEffect(() => {
    setPage(1);
  }, [search, kindFilter, localId]);

  // A ?product= link opens the page that contains that product.
  useEffect(() => {
    if (!highlightedProductId) return;
    const index = filteredProducts.findIndex((product) => product.id === highlightedProductId);
    if (index >= 0) setPage(Math.floor(index / PAGE_SIZE) + 1);
  }, [highlightedProductId, filteredProducts]);

  // ?local=<id> preselects that Local once the user's Locals are known.
  useEffect(() => {
    const requested = parseInt(searchParams.get('local') || '');
    if (requested && availableLocals.some((local) => local.id === requested)) {
      setLocalId(requested);
    }
  }, [availableLocals, searchParams, setLocalId]);

  useEffect(() => {
    if (!highlightedProductId || loading) return;
    document
      .getElementById(`warehouse-product-${highlightedProductId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedProductId, loading, products]);

  if (!localLoading && !localId) {
    return (
      <div className="warehouse-dashboard">
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noLocalAssigned')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="warehouse-dashboard">
      <div className="warehouse-dashboard__header">
        <h2 className="warehouse-dashboard__title">{t('warehouse.dashboard.title')}</h2>
        {availableLocals.length > 0 && (
          <SearchableSelect<number>
            className="warehouse-dashboard__local-select"
            options={availableLocals.map((local) => ({ value: local.id, label: local.name }))}
            value={localId}
            onChange={(value) => setLocalId(value)}
          />
        )}
        <Link to={localId ? `/products?local=${localId}` : '/products'}>
          <Button variant="primary" size="sm">
            {t('warehouse.dashboard.addProduct')}
          </Button>
        </Link>
      </div>

      {showExplainer && (
        <Card variant="elevated" className="warehouse-dashboard__explainer">
          <div className="warehouse-dashboard__explainer-header">
            <h3>{t('warehouse.dashboard.explainer.title')}</h3>
            <Button variant="ghost" size="sm" onClick={dismissExplainer}>
              {t('warehouse.dashboard.explainer.dismiss')}
            </Button>
          </div>
          <p>{t('warehouse.dashboard.explainer.intro')}</p>
          <p>{t('warehouse.dashboard.explainer.example')}</p>
        </Card>
      )}

      {error && <div className="warehouse-dashboard__error">{error}</div>}

      {!loading && products.length > 0 && (
        <div className="warehouse-dashboard__toolbar">
          <input
            type="text"
            className="warehouse-dashboard__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('warehouse.dashboard.searchPlaceholder')}
            aria-label={t('warehouse.dashboard.searchPlaceholder')}
          />
          <select
            className="warehouse-dashboard__kind-filter"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
            aria-label={t('warehouse.dashboard.kindFilter')}
          >
            <option value="all">{t('warehouse.dashboard.kindAll')}</option>
            <option value="drinks">{t('warehouse.dashboard.kindDrinks')}</option>
            <option value="foods">{t('warehouse.dashboard.kindFoods')}</option>
          </select>
        </div>
      )}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : products.length === 0 ? (
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noProducts')}</p>
        </Card>
      ) : filteredProducts.length === 0 ? (
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noResults')}</p>
        </Card>
      ) : (
        <>
          <div className="warehouse-dashboard__products">
            {pageProducts.map((product) => {
              const isHidden = product.stock.quantity <= product.stock.hide_threshold;
              const isBusy = busyId === product.id;
              const input = getAdjustInput(product.id);
              const amount = parseInt(input.quantity, 10) || 0;
              return (
                <Card
                  key={product.id}
                  id={`warehouse-product-${product.id}`}
                  variant="elevated"
                  className={`warehouse-dashboard__product ${product.id === highlightedProductId ? 'warehouse-dashboard__product--highlighted' : ''}`}
                >
                  <div className="warehouse-dashboard__product-header">
                    <h3>{product.name}</h3>
                    <StockIndicator
                      stockQuantity={product.stock.quantity}
                      maxStock={0}
                      minStockThreshold={product.stock.min_threshold}
                      isAvailable={!isHidden}
                    />
                  </div>

                  {isHidden && (
                    <div className="warehouse-dashboard__hidden-badge">⚠️ {t('warehouse.dashboard.hiddenEverywhere')}</div>
                  )}

                  <div className="warehouse-dashboard__venue-row">
                    <span className="warehouse-dashboard__venue-qty">{product.stock.quantity}</span>
                    <div className="warehouse-dashboard__adjust">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        className="warehouse-dashboard__adjust-qty"
                        value={input.quantity}
                        onChange={(e) => setAdjustInput(product.id, { quantity: e.target.value })}
                        aria-label={t('warehouse.dashboard.quantity')}
                        disabled={isBusy}
                      />
                      <input
                        type="text"
                        className="warehouse-dashboard__adjust-note"
                        value={input.note}
                        onChange={(e) => setAdjustInput(product.id, { note: e.target.value })}
                        placeholder={t('warehouse.dashboard.notePlaceholder')}
                        aria-label={t('warehouse.dashboard.notePlaceholder')}
                        disabled={isBusy}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isBusy || amount <= 0 || amount > product.stock.quantity}
                        onClick={() => handleAdjust(product.id, -1)}
                        title={t('warehouse.dashboard.removeHint')}
                      >
                        − {t('warehouse.dashboard.remove')}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={isBusy || amount <= 0}
                        onClick={() => handleAdjust(product.id, 1)}
                        title={t('warehouse.dashboard.addHint')}
                      >
                        + {t('warehouse.dashboard.add')}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <Pagination
            page={currentPage}
            pageSize={PAGE_SIZE}
            totalItems={filteredProducts.length}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};

export default WarehouseDashboard;
